import {
  Alert,
  AlertIcon,
  AlertDescription,
  AlertProps,
} from '@chakra-ui/react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { isOrderSameByFactory } from '../utils';
import { IconButton } from 'packages/common-frontend/components/IconButton';
import { FiLock, FiUnlock, FiRefreshCw } from 'react-icons/fi';

/**
 * Hook to use draggable row components
 * @param comparisonKey key used to compare values between intial + current row state to see if order has changed
 */
export function useDraggableRow<T extends Object>({
  comparisonKey,
}: {
  comparisonKey: keyof T;
}) {
  const [initialRows, setInitialRows] = useState<T[]>([]);
  const [rows, setRows] = useState<T[]>([]);
  const [isOrderLocked, setIsOrderLocked] = useState<boolean>(true);

  const isOrderSameBy = useCallback(
    (closureRows: T[], closureInitialRows: T[]) =>
      isOrderSameByFactory<T>(comparisonKey)(closureRows, closureInitialRows),
    [comparisonKey],
  );
  const isOrderSame = useMemo(() => isOrderSameBy(rows, initialRows), [
    rows,
    initialRows,
    isOrderSameBy,
  ]);

  const moveRow = useCallback((dragIndex: number, hoverIndex: number) => {
    setRows((prevRows: T[]) => {
      const newState = [...prevRows];
      const movingUser = newState.splice(dragIndex, 1)[0];
      newState.splice(hoverIndex, 0, movingUser);
      return newState;
    });
  }, []);

  type LockOrderButtonProps = {
    isDisabled?: boolean;
    onSaveOrder: (newState: T[]) => void;
  };
  // !TODO @eldarshamukhamedov Component should not be declared inside other components
  const LockOrderButton = useCallback(
    ({ isDisabled = false, onSaveOrder }: LockOrderButtonProps) => (
      <IconButton
        label={isOrderLocked ? 'Unlock order' : 'Save and Lock order'}
        ariaLabel={isOrderLocked ? 'Unlock order' : 'Lock order'}
        icon={isOrderLocked ? <FiLock /> : <FiUnlock />}
        colorScheme={isOrderLocked ? 'red' : 'green'}
        onClick={() => {
          setIsOrderLocked((previousIsOrderLocked) => {
            const newIsOrderLocked = !previousIsOrderLocked;
            if (newIsOrderLocked) {
              onSaveOrder(rows);
            }
            return newIsOrderLocked;
          });
        }}
        isDisabled={isDisabled}
      />
    ),
    [isOrderLocked, rows],
  );

  type RestoreOrderButtonProps = { isDisabled?: boolean };
  const RestoreOrderButton = useCallback(
    ({ isDisabled = false }: RestoreOrderButtonProps) =>
      !isOrderLocked && !isOrderSame ? (
        <IconButton
          label="Restore previous ordering"
          ariaLabel="restore previous ordering"
          icon={<FiRefreshCw />}
          onClick={() => {
            setRows(initialRows);
          }}
          isDisabled={isDisabled}
        />
      ) : null,
    [setRows, initialRows, isOrderLocked, isOrderSame],
  );

  type SaveOrderCalloutProps = {
    message?: string;
    status?: AlertProps['status'];
  };
  const SaveOrderCallout = useCallback(
    ({ message, status }: SaveOrderCalloutProps) => {
      return !isOrderLocked ? (
        <Alert status={status || 'warning'} mb="10px" mt="10px">
          <AlertIcon />
          <AlertDescription>
            {message ||
              `Lock changes before clicking 'Save Progress' or 'Commit,' or your changes will be lost. To add a new item, your changes must be locked.`}
          </AlertDescription>
        </Alert>
      ) : null;
    },
    [isOrderLocked],
  );

  return {
    useRowState: (initialState?: T[]) => {
      useEffect(() => {
        if (initialState && initialState.length > 0) {
          // if this is an empty array, we trigger a maximum recursion depth loop here without this check
          setInitialRows(initialState);
          setRows(initialState);
        }
      }, [initialState]);
      return [rows, setRows, initialRows] as const;
    },
    useOrderLockedState: () => {
      return [isOrderLocked, setIsOrderLocked] as const;
    },
    moveRow,
    isOrderSame,
    ui: {
      LockOrderButton,
      RestoreOrderButton,
      SaveOrderCallout,
    },
  };
}
